import React from 'react';
import { Link } from 'gatsby';
import RichText from 'components/RichText';
import styled from "@emotion/styled";
import dimensions from 'styles/dimensions';
import { Inner } from 'styles/structure';
import { Rubric } from 'styles/typography';
import Content from 'components/_ui/Content';
import colors from "styles/colors";
import Button from 'components/_ui/Button';
import Video from '../../vectors/video.svg';
import Illustration from 'components/_ui/Illustration';

const HeroContainer = styled.div`
  padding: 4rem 0 8rem;
  
  &.Hero--colored {
    background-color: ${colors.orange100}
  }
  
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    padding: 6.4rem 0 8.8rem; 
  }
`;

const HeroInner = styled(Inner)`
  @media(min-width: ${dimensions.tabletLandscapeUp}px) { 
    display: flex;
    align-items: center;
  }
`;

const HeroImage = styled.div`
  display: block;
  width: 100%;
  max-width: 40rem;
  
  img {
    display: block;
    width: 100%;
  }
  
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    flex: 1 1 50%;
    margin-right: 5.6vw;
    max-width: none;
    
    img {
      &.is-mobile {
        display: none;
      }
    }
    
    .Hero--right & {
      order: 2;
      margin-right: 0;
      margin-left: 5.6vw;
    }
  }
`;

const HeroContent = styled.div`
  margin-top: 4rem;
  
  h1,
  h2,
  h3,
  h4 {
    color: ${colors.teal800};
    
    & + * {
      margin-top: 2.4rem;
    }
  }
  
  p {
    line-height: 1.66;
    max-width: 80rem;
  }
  
  em {
    position: relative;
    font-style: normal;
    white-space: nowrap;

    &::after {
      content: '';
      position: absolute;
      bottom: -0.2rem;
      left: 0;
      width: 100%;
      height: 0.2rem;
      background-color: ${colors.teal800};
    }
  }
  
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    flex: 2 1 50%;
    max-width: 56.8rem;
    
    &:only-child {
      text-align: center;
      flex-basis: 100%;
      max-width: 80rem;
      margin: 0 auto;
      
      p {
        max-width: none;
      }
    }
    
    p {
      max-width: 90%;
    }
  }
  
  @media(min-width: ${dimensions.desktopUp}px) {
    .Hero--large & {
      p {
        font-size: 2.1rem;
      }
    }
  }
`;

const HeroButtons = styled.div`
  margin-top: 5.6rem;
  
  > * {
    display: block;
    width: 100%;
    
    & + * {
      margin-top: 2.4rem;
    }
  }
  
  @media(min-width: ${dimensions.tabletPortraitUp}px) {
    display: flex;
    
    > * {
      width: auto;
      margin-top: 0;
      & + * {
        
        margin-left: 2rem;
      }
    }
  }
`;

const Hero = ({ heading, rubric, content, image, illustration, buttons, variant, size, imagePosition }) => {
  let heroHeading;

  if (heading && heading[0] && heading[0].text) {
    heroHeading = <RichText render={heading}/>;
  }  else if (heading) {
    heroHeading = size === 'large' ? <h1>{heading}</h1> : <h2>{heading}</h2>;
  }

  return (
    <HeroContainer className={`Hero ${variant ? `Hero--${variant}` : ''} ${imagePosition ? `Hero--${imagePosition}` : ''} ${size ? `Hero--${size}` : ''}`}>
      <HeroInner>
        {(illustration || image) && (
          <HeroImage>
            {illustration && (
              <>
                <Illustration source={illustration} variant='hero' />
                {image && <img className='is-mobile' src={image.url} alt={image.alt} loading="lazy" />}
              </>
            )}

            {(!illustration && image) && (
              <img src={image.url} alt={image.alt} loading="lazy" />
            )}

          </HeroImage>
        )}

        <HeroContent>
          {rubric && (
            <Rubric>{rubric}</Rubric>
          )}

          {heading && heroHeading}

          {content && (
            <Content content={content} />
          )}

          {buttons && (
          <HeroButtons>
            {buttons.map((button, i) => {
              if (button.icon === 'video') {
                return (
                  <Button
                    as={'a'}
                    href={button.destination}
                    key={i}
                    className={button.style && `Button--${button.style}`}
                  >
                    <Video/>
                    <span>{button.text}</span>
                  </Button>
                )
              } else {
                return (
                  <Button
                    as={Link}
                    to={button.destination}
                    key={i}
                    className={button.style && `Button--${button.style}`}
                  >
                    <span>{button.text}</span>
                  </Button>
                )
              }
            })}
          </HeroButtons>
          )}

        </HeroContent>
      </HeroInner>
    </HeroContainer>
  );
};

export default Hero;
